import React, {useEffect} from 'react';

import AemComponent from '@/components/Aem/AemComponent/AemComponent';
import EditableText from '@/components/Aem/EditableText/EditableText';
import {AppButton} from '@/components/Button';
import appConfig from '@/config/appConfig.typed';
import * as advanceDiagnosticIconTypes from '@/consts/advanceDiagnostic/advanceDiagnosticIconTypes';
import * as advanceDiagnosticScreenTypes from '@/consts/advanceDiagnostic/advanceDiagnosticScreenTypes';
import {
    ADVANCED_DIAGNOSTICS_ACCEPT_THIRD_STEP_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_CURE_MARKET_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_ELIGIBILITY_CHECK_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_NOT_CURE_MARKET_BUTTON_ARIA_LABEL,
} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import SupportAndStoreTemplate from '@/containers/AdvancedDiagnosticPage/components/SupportAndStoreTemplate';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeADTracking from '@/services/analytics/amplitude/amplitudeADTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';

import AdvancedDiagnosticDescription from '../components/AdvancedDiagnosticDescription';
import AdvancedDiagnosticIcon from '../components/AdvancedDiagnosticIcon';
import AdvancedDiagnosticLayout from '../components/AdvancedDiagnosticLayout';
import AdvancedDiagnosticTitle from '../components/AdvancedDiagnosticTitle';
import NotNowButtonAD from '../components/NotNowButtonAD';
import useADMarketScreen from './useADMarketScreen';

export const AdvancedDiagnosticQuestionnaireMarketScreen = () => {
    const {inferenceCode, redirectToSupport, isHolder, isQuestionnaireThirdStep} = useADMarketScreen();
    const isQureMarket = appConfig.getIsQureMarket();

    const itemNameTitle = isQureMarket
        ? isQuestionnaireThirdStep
            ? 'quremarketthirdsteptitle'
            : 'quremarkettitle'
        : 'notquremarkettitle';

    const itemNameDescription = isQureMarket
        ? isQuestionnaireThirdStep
            ? 'quremarketthirdstepdesc'
            : 'quremarketdesc'
        : 'notquremarketdesc';

    const itemNameAcceptButtonText = isQuestionnaireThirdStep ? 'acceptthirdstepbutton' : 'acceptbutton';
    const buttonAriaLabel = isQuestionnaireThirdStep
        ? ADVANCED_DIAGNOSTICS_ACCEPT_THIRD_STEP_BUTTON_ARIA_LABEL
        : ADVANCED_DIAGNOSTICS_ELIGIBILITY_CHECK_BUTTON_ARIA_LABEL;

    useEffect(() => {
        amplitudeADTracking.trackADInitialQuestionnaireScreen();
        googleAnalyticsService.trackAdInitialQuestionnaireScreenView();
    }, []);

    const NotNowButton = ({itemName, isPrimary, ariaLabelKey}) => (
        <NotNowButtonAD
            className='w-full'
            isHolder={isHolder}
            inferenceCode={inferenceCode}
            screenName={advanceDiagnosticScreenTypes.QUESTIONNAIRE_MARKET_SCREEN}
            isQuestionnaireThirdStep={isQuestionnaireThirdStep}
            isPrimary={isPrimary}
            aria-label={getAriaLabel(ariaLabelKey)}
        >
            <AemComponent component={EditableText} pageName={routePaths.ADVANCE_DIAGNOSTIC} itemName={itemName} />
        </NotNowButtonAD>
    );

    return (
        <AdvancedDiagnosticLayout>
            <AdvancedDiagnosticLayout.ContentWrapper>
                <AdvancedDiagnosticLayout.Container>
                    <div className='mt-10 flex h-full w-full flex-col justify-between md:w-auto ml:mt-8 ml:justify-center'>
                        <div className='flex flex-1 flex-col items-center justify-center ml:mb-8 ml:h-auto ml:flex-none'>
                            <AdvancedDiagnosticIcon className='h-12 w-12' type={advanceDiagnosticIconTypes.ERROR} />
                            <AdvancedDiagnosticTitle itemName={itemNameTitle} />
                            <AdvancedDiagnosticDescription itemName={itemNameDescription} />
                        </div>

                        {isQureMarket ? (
                            <div className='w-full'>
                                <AppButton
                                    primary
                                    light
                                    className='mb-4 mt-0 w-full min-w-[312px]'
                                    aria-label={getAriaLabel(buttonAriaLabel)}
                                    onClick={redirectToSupport}
                                >
                                    <AemComponent
                                        component={EditableText}
                                        pageName={routePaths.ADVANCE_DIAGNOSTIC}
                                        itemName={itemNameAcceptButtonText}
                                    />
                                </AppButton>
                                <NotNowButton
                                    itemName='quremarketbutton'
                                    ariaLabelKey={ADVANCED_DIAGNOSTICS_CURE_MARKET_BUTTON_ARIA_LABEL}
                                />
                            </div>
                        ) : (
                            <div className='w-full'>
                                <SupportAndStoreTemplate className='mb-4 mt-0 w-full ml:mb-10' />
                                <NotNowButton
                                    isPrimary
                                    itemName='notquremarketbutton'
                                    ariaLabelKey={ADVANCED_DIAGNOSTICS_NOT_CURE_MARKET_BUTTON_ARIA_LABEL}
                                />
                            </div>
                        )}
                    </div>
                </AdvancedDiagnosticLayout.Container>
            </AdvancedDiagnosticLayout.ContentWrapper>
        </AdvancedDiagnosticLayout>
    );
};

export default AdvancedDiagnosticQuestionnaireMarketScreen;
